import React, { useMemo } from 'react'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Paper } from '@mui/material'
import { ChargingCardModel } from '../api/models/charging-card-model'
import { useTableStyles } from '../../shared/styles/table.styles'
import { Header, TableComplete } from '@e3dc-react/shell/Elements/Table'
import { ChargingCardsMobileTableRowItem } from './ChargingCardsMobileTableRowItem'
import { useTypedSelector } from 'src/State/RootReducer'
import { mobileSortFn } from '../../shared/utils/sort-mobile-table'

interface ChargingCardsTableMobileViewProps {
  chargingCards: ChargingCardModel[]
  installationId: number
  installationHasWallboxes: boolean
}

/**
 * Component is visible on mobile and tablet screen/view.
 * It holds rows as accordions for charging cards table
 * @param chargingCards - array of ChargingCardModel
 */
export const ChargingCardsTableMobileView: React.FC<ChargingCardsTableMobileViewProps> = ({
  chargingCards,
  installationId,
  installationHasWallboxes,
}) => {
  const { t } = useCustomTranslation()
  const { classes } = useTableStyles()

  const chargingCadsMobileSort = useTypedSelector(state => state.eMobility.chargingCardsMobileSort.sort)
  const sortBy = chargingCadsMobileSort?.name ?? 'tokenId'
  const sortDirection = chargingCadsMobileSort?.direction ?? 'asc'

  const isManagedMode = chargingCards.some(card => card.linkedWallboxes.length > 0)

  const header = useMemo<Header<ChargingCardModel>[]>(() => {
    return [
      {
        value: sortBy,
        label: t('emobility.chargingCards.chargingCard'),
        sortFunction: (() => {
          if (sortBy === 'linkedWallboxes') {
            return (a: ChargingCardModel['linkedWallboxes'], b: ChargingCardModel['linkedWallboxes']) => a.length - b.length
          }

          return mobileSortFn
        })(),
        isSortable: false,
        valueRender: (_, __, item, rowIndex) => {
          if (!item || rowIndex === undefined) return <></>

          return (
            <ChargingCardsMobileTableRowItem
              chargingCard={item}
              installationId={installationId}
              isManagedMode={isManagedMode}
              installationHasWallboxes={installationHasWallboxes}
            />
          )
        },
      },
    ]
  }, [installationHasWallboxes, installationId, isManagedMode, sortBy, t])

  return (
    <Paper className={classes.tableContainerMobile}>
      <TableComplete
        key={`${sortBy}-${sortDirection}`}
        header={header}
        data={chargingCards}
        bodyElevation={0}
        footerElevation={0}
        headerElevation={0}
        rowClassName={classes.row}
        className={classes.table}
        headerClassName={classes.tableHeader}
        coloredColumn
        initialSort={{
          name: sortBy,
          direction: sortDirection ?? 'desc',
        }}
      />
    </Paper>
  )
}
